import { Dispatch, SetStateAction } from "react";
import { categoriesMap, propertyTypeObject, QuickAccess, toolInputKeyForQuickAccess } from "utils/constants";
import {
  mlsStatusOptions,
  mlsStatusLoggedinOptions,
  baseAmenitiesLabels,
  countiesLookup,
  toolInputAmenitiesOptions,
  normalizeFinanceValue,
} from "utils/filtersData";
import {
  getLongValue,
  getShortValue,
  joinWithSeparator,
  mapPropertyClassIdsToNames,
  mapNamesToPropTypeCodes,
  mapPropTypeCodesToNames,
  splitIntoArray,
  typedEntries,
  typedKeys,
} from "utils/helpers";
import {
  SCHOOL_KEYS,
  SchoolKey,
} from "components/AiSummaryDrawer/AiSummaryDrawer.def";
import {
  getCommunityDetailsById,
  getDistrictSchoolDetailsById,
  getNeighborhoodDetailsById,
  getSchoolsDetailsByIds,
} from "services";
import {
  BaseAmenities,
  DRIVETIME_FILTER_KEYS,
  copyDriveTimeAsStrings,
  promoteLegacyDriveTime,
  ISearchListingsFilters,
  ToolFilterAmenities,
} from "types/filters/search-listings-filters";
import { ILabeledField, toLabeledField } from "operations/search-filter-operation";
import { ISearchListingsToolInput } from "types/inputs/search-listings-input";
import { SingleOrArray } from "types/utility";

// AI/backend availability labels that don't equal the MLS dropdown code.
// The backend's availabilityEnum sends human-readable labels (e.g. "Active",
// "WITH") that need to map to the dropdown's MLS codes ("A", "with").
const AVAILABILITY_LABEL_TO_VALUE: Record<string, string> = {
  active: "A",
  withdrawn: "with",
  sold: "closd",
  rented: "closd",
};

// Helper function to normalize status value - case insensitive matching
const normalizeStatusValue = (incomingValue: string): string => {
  if (!incomingValue) return "";

  // Combine both logged-in and non-logged-in status options, avoiding duplicates by value
  const allStatusOptions = [
    ...mlsStatusLoggedinOptions,
    ...mlsStatusOptions.filter(
      (option) =>
        !mlsStatusLoggedinOptions.some(
          (loggedInOption) => loggedInOption.value === option.value,
        ),
    ),
  ];

  // Find matching status by comparing lowercase values
  const matchedOption = allStatusOptions.find(
    (option) => option.value.toLowerCase() === incomingValue.toLowerCase(),
  );

  if (matchedOption) return matchedOption.value;

  // Fall back to the label-to-value map for AI-provided availability labels
  // that don't equal the MLS dropdown code (e.g. "Active" -> "A").
  return (
    AVAILABILITY_LABEL_TO_VALUE[incomingValue.toLowerCase()] ?? incomingValue
  );
};

export type FilterAmenities = BaseAmenities
  | "pool"
  | "no_pool";

const filterAmenitiesLabels: Record<FilterAmenities, string> = {
  ...baseAmenitiesLabels,
  pool: "Private Pool",
  no_pool: "No Private Pool",
};


export const filterAmenitiesOptions = typedEntries(filterAmenitiesLabels).map(([value, label]) => ({ value, label }));

export interface IFilters {
  neighborhood?: ILabeledField<string>;
  community?: ILabeledField<number>[];
  nid?: ILabeledField<string>;
  middle_school?: ILabeledField<string>;
  elemantary_school?: ILabeledField<string>;
  school_district?: ILabeledField<string>;
  high_school?: ILabeledField<string>;
  school_id?: ILabeledField<string>;
  amenities?: FilterAmenities[];
  Category?: Array<QuickAccess>;
  PropertyType?: string[];
  Status?: string;
  // Legacy status key from older tool versions; retained for parity, no UI consumer.
  PropertyStatus?: string;
  KeyMap?: string;
  For?: string;
  Address?: string;
  City?: string[];
  ZipCode?: string[];
  Subdivisions?: string;
  County?: ILabeledField<string>;
  MLSNumber?: string;
  MinimumPrice?: string;
  MaximumPrice?: string;
  marketarea?: string;
  MLSArea?: string;
  BedroomMin?: string;
  BedroomMax?: string;
  FullbathMin?: string;
  FullbathMax?: string;
  HalfbathAvailability?: string;
  HoafeeMax?: string;
  Style?: string;
  Stories?: string[];
  NewConstr?: string;
  Parking?: string;
  Finance?: string;
  GarageNum?: string;
  GarageDesc?: string;
  YearbuildsMin?: string;
  YearbuildsMax?: string;
  buildingSQFTMin?: string;
  buildingSQFTMax?: string;
  PricesqftMin?: string;
  PricesqftMax?: string;
  LotsizeMin?: string;
  LotsizeMax?: string;
  LotAcresMin?: string;
  LotAcresMax?: string;
  listing_agentid?: ILabeledField<string>;
  //open_houses?: number;
  //new_constr?: string;
  sort?: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  map_tools_nwlat?: number;
  map_tools_nwlng?: number;
  map_tools_selat?: number;
  map_tools_selng?: number;
  map_tools_polygon?: string;
  // Drive-time search (har.com's "Drive Time" location mode). The three
  // user-facing values are edited in the modal's Drive Time block; the center
  // is the server's geocode of the address and is dropped whenever the address
  // changes so the next search geocodes the new one.
  drivetime_address?: string;
  drivetime_arrival_time?: string;
  drivetime_travel_time?: string;
  drivetime_center?: string;
  extra?: Record<string, string>;
}

// Geo-scoping keys (radius search / map bounds / drawn polygon). They have no
// filter-modal control, but must survive transformApiFilters → the /getsummary
// query string so the AI summary stays restricted to the searched area.
const GEO_FILTER_KEYS = [
  "latitude",
  "longitude",
  "radius",
  "map_tools_nwlat",
  "map_tools_nwlng",
  "map_tools_selat",
  "map_tools_selng",
  "map_tools_polygon",
] as const;

export interface IFiltersProps {
  setFilters: Dispatch<SetStateAction<IFilters>>;
  filters: IFilters;
  closePopup: () => void;
  send?: (filters: IFilters) => void;
}

/** Filter state a search starts from, and the one Reset returns to. "For" needs
 *  a real value here because its radio group has no unselected rendering — the
 *  "For Sale" radio shows checked whenever the value isn't "Rent" — so leaving
 *  it empty makes the modal display a criterion that Apply never sends. */
export const DEFAULT_FILTERS: IFilters = { For: "Sale" };

// Join a scalar/array filter value into a comma-separated string. Arrays are
// joined directly; scalars pass through `joinWithSeparator` (trim/empty-filter).
const arrToStr = (
  arr: string | string[] | number[] | undefined,
): string => (Array.isArray(arr) ? arr.join(",") : joinWithSeparator(",", arr));

// Narrow a possibly-legacy half-bath value to its `{ half }` object form.
// Scalars (number/boolean) and nullish values yield `undefined`, matching the
// original runtime behavior where `(number).half` was simply undefined.
const asLegacyHalfBath = (
  v: unknown,
): ISearchListingsFilters.LegacyHalfBath | undefined =>
  v != null && typeof v === "object"
    ? (v as ISearchListingsFilters.LegacyHalfBath)
    : undefined;

/**
 * Normalize any county value we may receive into the labeled field the UI holds.
 *
 * County arrives in four shapes depending on the path: a FIPS code as a number
 * (`fips_code` from the tool artifact) or a string, an `ILabeledField` (the
 * operation shapes), a single-element array, or a display NAME ("Harris") from
 * the legacy `county` key. The modal's <select> and the AI-summary query string
 * both key on the FIPS code, so a name left as-is silently disables the filter.
 *
 * The label comes from the static `countiesLookup` — there is no counties
 * endpoint — and an unknown code keeps an empty label so the raw value still
 * round-trips back to `fips_code`.
 */
const toCountyField = (
  raw:
    | number
    | string
    | ILabeledField<number>
    | ILabeledField<string>
    | Array<number | string>
    | undefined,
): ILabeledField<string> | undefined => {
  const first = Array.isArray(raw) ? raw[0] : raw;
  if (first == null || first === "") { return undefined; }

  const field = toLabeledField(first as number | string);
  if (field?.value == null || field.value === "") { return undefined; }

  const text = String(field.value);
  // A display name (legacy `county`) has to be mapped back to its FIPS code;
  // anything numeric is already the code.
  const value = /^\d+$/.test(text) ? text : getShortValue(countiesLookup, text);
  if (!value) { return undefined; }

  return { value, label: field.label || getLongValue(countiesLookup, value) || "" };
};

/**
 * Neighborhood id (`nid` / the tool's `neighborhood_id`), normalized to a single
 * labeled field. It arrives as a raw id, as `{ value, label }` (the server
 * resolves the name in `_resolve_neighborhood`), or — from an older tool input —
 * wrapped in a one-item array. `toLabeledField(x, mapper)` rebuilds the field and
 * drops the label, so the name is carried over explicitly: without it the pill
 * renders "1621" instead of "River Oaks".
 */
const toNeighborhoodField = <K extends number | string>(
  raw:
    | number
    | string
    | ILabeledField<number>
    | ILabeledField<string>
    | Array<number | string | ILabeledField<number> | ILabeledField<string>>
    | undefined,
  mapper: (value: number | string) => K,
): ILabeledField<K> | undefined => {
  const first = Array.isArray(raw) ? raw[0] : raw;
  if (first == null || first === "") { return undefined; }

  const label = typeof first === "object" && "label" in first ? first.label : null;
  const field = toLabeledField(first as number | string, mapper);
  if (field?.value == null || Number.isNaN(field.value as unknown as number)) { return undefined; }

  return { value: field.value, label: label || field.label };
};

/**
 * Master-planned community, normalized to the list of labeled fields the UI
 * holds (the modal pill, `processUIFilters` and `fromUIToFilters` all read it as
 * one). It arrives as a bare id on artifacts written since the tool took exactly
 * one community (`community: Optional[int]`), and as a list — possibly empty —
 * on chats saved before that. `toLabeledField` keeps a scalar a scalar, so the
 * bare id used to reach the UI as a single object: every `community[0]` read came
 * back undefined, which crashed the AI-summary query and dropped the community
 * from the modal and from Apply (CB-1799).
 *
 * Only the first community is kept, as `processUIFilters` does: an older chat may
 * hold several, and the tool rejects more than one. A HAR.com URL seed is not
 * clamped (it never passes through here), so the backend can still ask the user
 * to pick one rather than silently searching the first.
 */
const toCommunityField = (
  raw: SingleOrArray<number | ILabeledField<number>> | null | undefined,
): ILabeledField<number>[] | undefined => {
  const first = [raw ?? []].flat().find(
    (item) => (typeof item === "object" ? item?.value : item) != null,
  );
  return first != null ? [toLabeledField(first)] : undefined;
};

/**
 * Fill in the school district's NAME when we only hold its ID.
 *
 * The district reaches the UI as an ID far more often than as a name: HAR.com's
 * search URL spells its district param `school_district` and puts an ID in it,
 * and the filter modal stores the ID too. The server resolves the name in
 * `_resolve_school_district`, but that only lands when its reply does — the
 * optimistic artifact built client-side (`SearchFilterOperation.buildUserMetadata`)
 * carries `label: null`, so the pill would read "27" for as long as the search runs.
 *
 * Resolving here closes that window. `getDistrictSchoolDetailsById` is cached with
 * a TTL and de-duplicates in-flight requests, so repeat calls are free.
 *
 * The field is returned UNCHANGED whenever a lookup can't improve it:
 * - it already carries a label — INCLUDING an empty one. `label` is `string | null`,
 *   and `""` is a value, not an absence: the server writes it (`_labeled_field(id, "")`)
 *   when it asked HAR and there is no such district, so re-asking from here can only
 *   404 again. A server lookup that never got an answer (the service threw) leaves the
 *   field a bare string instead, i.e. `label: null` — and that one we do retry.
 *   `getDistrictSchoolDetailsById` does not cache a miss, so treating `""` as unlabeled
 *   would re-request on every artifact change, forever, to no effect.
 * - its value isn't all digits (an LLM-supplied name like "Katy ISD" — there is
 *   nothing to look up, and the lookup would 404);
 * - the lookup fails or finds no district.
 *
 * That last case matters: the previous inline version overwrote the field with the
 * lookup result unconditionally, so a miss collapsed it to `{ value: "", label: "" }`
 * and silently dropped the district from the filter modal.
 */
export const resolveSchoolDistrictLabel = async (
  field: ILabeledField<string> | undefined,
): Promise<ILabeledField<string> | undefined> => {
  if (!field?.value || field.label != null) { return field; }

  // Trim once and use that one value for the test, the lookup and the fallback:
  // `getDistrictSchoolDetailsById` keys its cache on what it is handed and puts it
  // straight into the request path, so validating `" 27 "` as `"27"` and then
  // fetching the padded form would miss the cache and 404 on the way out.
  const id = String(field.value).trim();
  if (!/^\d+$/.test(id)) { return field; }

  const district = await getDistrictSchoolDetailsById(id);
  if (!district?.name) { return field; }

  return { value: String(district.id || id), label: district.name };
};

/**
 * Whether an artifact predates CB-1788, i.e. its values are percent-encoded.
 *
 * Filters are raw now: the AI side percent-encodes at its HAR transport layer
 * (har_api_call) and nowhere else, so decoding them again would corrupt them.
 * But `city` WAS pre-encoded before CB-1788, and chats persisted then still
 * replay through here carrying "San%20Antonio".
 *
 * The two cannot be told apart by inspecting the value: "A%26B" is a valid
 * encoding of "A&B" and equally a plausible raw string, and narrowing the sniff
 * to escapes a place name might plausibly carry drops real legacy values such
 * as "Washington%2C%20DC". So `restructure_filters` now stamps what it emits,
 * and the absence of that stamp is what identifies an older artifact.
 */
const isLegacyEncodedArtifact = (x: { filters_encoding?: string } | null | undefined): boolean =>
  x?.filters_encoding !== "raw";

/** Decodes a legacy value, leaving it as-is if it is not valid encoding. */
const decodeLegacyValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const toUIFilters = (input: ISearchListingsFilters | ISearchListingsFilters.Raw): IFilters => {
  // Shallow-copy the input so `toUIFiltersLegacyTypes` can *detach* legacy-typed
  // fields (via `delete`) without mutating the caller's object. Callers pass
  // `artifact.filters` — a React-owned value that also feeds a `JSON.stringify`
  // dedup key in MlsSearchCard — so in-place mutation would corrupt that key.
  const x = { ...input } as typeof input;
  const filters: Partial<IFilters> = {};

  toUIFiltersLegacyTypes(filters, x);

  if (x?.listing_agentid) {
    filters.listing_agentid = toLabeledField(x.listing_agentid);
  }

  const community = toCommunityField(x?.community);
  if (community) {
    filters.community = community;
  }

  if (x?.nid) {
    filters.nid = toLabeledField(x.nid, String);
  }

  if (x?.marketarea) {
    filters.marketarea = x.marketarea;
  }

  if (x?.zip_code) {
    filters.ZipCode = x.zip_code;
  }

  if (x?.subdivisions) {
    filters.Subdivisions = x.subdivisions;
  }

  if (x?.streetaddress) {
    filters.Address = x.streetaddress;
  }

  if (x?.fips_code) {
    filters.County = toCountyField(x.fips_code);
  }

  if (x?.finance) {
    // Canonicalize: the value may arrive percent-encoded or without the
    // `Seller to ` prefix, and both the pills and the `[name=Finance]` <select>
    // match `financeOptions` by strict equality.
    filters.Finance = normalizeFinanceValue(x.finance);
  }

  if (x?.property_class_id) {
    // The modal's checkboxes key on property-type NAMES, but native-sourced
    // `property_class_id` holds numeric ids — convert so the selection is not lost.
    filters.PropertyType = mapPropertyClassIdsToNames(x.property_class_id);
  } else if (x?.prop_type) {
    // Rental filters carry `prop_type` codes instead: search_listings_tool clears
    // `property_class_id` when it sets `prop_type`, and the tool artifact passes
    // the field straight through — so without this branch re-opening the modal on
    // a for-rent result set shows no property types selected.
    filters.PropertyType = mapPropTypeCodesToNames(x.prop_type);
  }

  const categories: Set<QuickAccess> = new Set();
  for(const key of typedKeys(categoriesMap)) {
    const value = x?.[key];
    if(!value) { continue; }
    if(key === 'new_constr2' && x.new_constr2 !== 'Yes') { continue; }
      
    categories.add(categoriesMap[key]);
  }

  if(categories.size > 0) {
    filters.Category = Array.from(categories);
  }

  if (x?.all_status) {
    // `all_status` is a comma-separated string; take the first status token
    // (indexing the string directly would yield its first character).
    const first = splitIntoArray(x.all_status)[0];
    filters.Status = first ? normalizeStatusValue(first) : "";
  } else if (x?.sold) {
    filters.Status = "closd";
  }

  if (
    x?.for_sale !== undefined &&
    (!x?.sold || !!x.all_status?.includes("closd"))
  ) {
    // Loose `==`: the query-string path supplies `for_sale` as the string "1".
    filters.For = x.for_sale == 1 ? "Sale" : "Rent";
  }

  if (x?.city) {
    const decodeCity = isLegacyEncodedArtifact(x);
    filters.City = x.city
      .split(",")
      .map((c: string) => (decodeCity ? decodeLegacyValue(c.trim()) : c.trim()));
    // let city: string | string[] | undefined;
    // if (Array.isArray(x.city)) {
    //   city = decodeURIComponent(arrToStr(x.city));
    // } else if (typeof x.city === "string") {
    //   city = x.city.split(",").map((c: string) => decodeURIComponent(c.trim()));
    // }
    // filters.City = city;
  }

  if (x?.mlsnum) {
    filters.MLSNumber = x.mlsnum;
  }

  if (x.listing_price_min) {
    filters.MinimumPrice = String(x.listing_price_min);
  }

  if (x.listing_price_max) {
    filters.MaximumPrice = String(x.listing_price_max);
  }

  // Add `x.bedrooms` to `filters` if `x.bedrooms.min` or `x.bedrooms.max` exists
  if (x?.bedroom_min) {
    filters.BedroomMin = String(x.bedroom_min);
  }
  if (x?.bedroom_max) {
    filters.BedroomMax = String(x.bedroom_max);
  }

  // Add `x.baths` to `filters` if `x.baths.min` or `x.baths.max` exists
  if (x?.full_bath_min) {
    filters.FullbathMin = String(x.full_bath_min);
  }
  if (x?.full_bath_max) {
    filters.FullbathMax = String(x.full_bath_max);
  }

  if (x?.half_bath_num != null) {
    filters.HalfbathAvailability = x.half_bath_num ? "1" : "0";
  }

  if (x?.hoa_fee_max) {
    filters.HoafeeMax = String(x.hoa_fee_max);
  }

  if (x?.style) {
    filters.Style = x.style;
  }

  if (x?.stories) {
    filters.Stories = x.stories;
    // filters.Stories = Array.isArray(x.stories)
    //   ? x.stories
    //   : x.stories.split(",") || [];
  }

  if (x?.new_constr) {
    filters.NewConstr = x.new_constr;
  }

  if (x?.parking) {
    filters.Parking = String(x.parking);
  }

  if (x?.garage_num) {
    filters.GarageNum = String(x.garage_num);
  }

  if (x?.garage_desc) {
    filters.GarageDesc = x.garage_desc;
  }

  if (x?.year_built_min) {
    filters.YearbuildsMin = String(x.year_built_min);
  }

  if (x?.year_built_max) {
    filters.YearbuildsMax = String(x.year_built_max);
  }

  if (x?.square_feet_min) {
    filters.buildingSQFTMin = String(x.square_feet_min);
  }

  if (x?.square_feet_max) {
    filters.buildingSQFTMax = String(x.square_feet_max);
  }
  
  if (x.price_sqft_min) {
    filters.PricesqftMin = String(x.price_sqft_min);
  }

  if (x.price_sqft_max) {
    filters.PricesqftMax = String(x.price_sqft_max);
  }

  if (x?.lotsize_min) {
    filters.LotsizeMin = String(x.lotsize_min);
  }

  if (x?.lotsize_max) {
    filters.LotsizeMax = String(x.lotsize_max);
  }

  if (x?.amenities) {
    filters.amenities = x.amenities.map((value): FilterAmenities => {
      if(value === "private_pool") { return 'pool'; }
      else if(value === "no_private_pool") { return 'no_pool'; }
      return value;
    });
  }

  if (x?.acres_min) filters.LotAcresMin = String(x.acres_min);
  if (x?.acres_max) filters.LotAcresMax = String(x.acres_max);

  if (x?.schoolelementary) {
    filters.elemantary_school = toLabeledField(x.schoolelementary);
  }
  if (x?.schoolmiddle) {
    filters.middle_school = toLabeledField(x.schoolmiddle);
  }
  if (x?.schoolhigh) {
    filters.high_school = toLabeledField(x.schoolhigh);
  }
  if (x?.school_district) {
    filters.school_district = toLabeledField(x.school_district);
  }
  if (x?.schoolid) {
    filters.school_id = toLabeledField(x.schoolid, String);
  }

  filters.sort = x.sort;

  // Geo-scoping keys (radius / map bounds) have no filter-modal control but must
  // survive to the /getsummary query string so the AI summary stays area-restricted.
  for(const key of GEO_FILTER_KEYS)
  {
    const value = x[key];
    if (value != null && value !== undefined) {
      (filters as Record<string, unknown>)[key] = value;
    }
  }

  // Drive time: strings on the artifact already, copied by name.
  copyDriveTimeAsStrings(x, filters);

  // Extra fields (from HAR.com native filters)
  if (x?.extra) {
    filters.extra = { ...x.extra };
  }
  promoteLegacyDriveTime(filters);

  // Transforming Legacy Filters
  toUIFiltersLegacy(filters, x);

  return filters;
};

function toUIFiltersLegacyTypes(filters: IFilters, x: ISearchListingsFilters.WithLegacy | ISearchListingsFilters.Raw.WithLegacy): void {
  // City: older output sent an array instead of the current comma-separated string.
  if(x?.city && Array.isArray(x.city)) {
    const decodeCity = isLegacyEncodedArtifact(x);
    filters.City = x.city.map((c: string) =>
      decodeCity ? decodeLegacyValue(c.trim()) : c.trim(),
    );
    delete x.city;
  }

  // ZipCode: older output sent a comma-separated string instead of the current string[].
  if (x?.zip_code && !Array.isArray(x.zip_code)) {
    filters.ZipCode = splitIntoArray(x.zip_code);
    delete x.zip_code;
  }

  // Stories: older output sent a comma-separated string instead of the current string[].
  if (x?.stories && !Array.isArray(x.stories)) {
    filters.Stories = splitIntoArray(x.stories);
    delete x.stories;
  }

  // Subdivisions: older output sent an array instead of the current single string.
  if (x?.subdivisions && Array.isArray(x.subdivisions)) {
    filters.Subdivisions = arrToStr(x.subdivisions);
    delete x.subdivisions;
  }
}

/**
 * Handle filter keys emitted by previous tool versions (and by the
 * query-string / MLS-card pre-processing paths) that `restructure_filters` no
 * longer returns. Runs after `toUIFilters` so legacy values take
 * precedence over their current-shape equivalents (e.g. `county` over
 * `fips_code`, `mls_number` over `mlsnum`), preserving the original ordering.
 */
function toUIFiltersLegacy(
  filters: IFilters,
  x: ISearchListingsFilters.WithLegacy | ISearchListingsFilters.Raw.WithLegacy
): void {
  if (x?.neighborhood && !filters.neighborhood) {
    filters.neighborhood = toLabeledField(x.neighborhood);
  }

  if (x?.property_status) {
    filters.PropertyStatus = x.property_status;
  }

  // Legacy quick-access category inputs. Historically collected into a Set that
  // was never written back to `filters`; retained here for parity.
  const categories: Set<QuickAccess> = new Set(filters.Category ?? []);
  if (x?.Category) {
    x.Category.forEach((c) => categories.add(c));
  }
  if (x?.quick_access) {
    x.quick_access.forEach((c) => categories.add(c));
  }

  if(categories.size > 0) {
    filters.Category = Array.from(categories);
  }

  // The legacy key carries the county's display NAME, not its FIPS code, so it
  // has to be mapped back before the modal's <select> can match it.
  if (x?.county && !filters.County) {
    filters.County = toCountyField(x.county);
  }

  // Some producers send the tool-input county key instead of `fips_code`.
  if (x?.county_id != null && !filters.County) {
    filters.County = toCountyField(x.county_id);
  }

  if (x?.mls_number && !filters.MLSNumber) {
    filters.MLSNumber = arrToStr(x.mls_number);
  }

  // Legacy property-type key (renamed to `property_class_id` in current output).
  if (x?.property_type && !filters.PropertyType) {
    filters.PropertyType = x.property_type;
  }

  // Legacy half-bath nested inside the baths object.
  if(!filters.HalfbathAvailability)
  {
    if (
      x?.baths_bathrooms?.half_bath_num != null ||
      x?.baths_bathrooms?.HalfbathAvailability != null
    ) {
      const val = x.baths_bathrooms.half_bath_num ?? x.baths_bathrooms.HalfbathAvailability;
      filters.HalfbathAvailability = val ? "1" : "0";
    }

    // Legacy standalone half-bath scalar (`half_bath_num` takes precedence, as in
    // the original combined block).
    if (x?.half_bath_num != null || x?.HalfbathAvailability != null) {
      const val = x.half_bath_num ?? x.HalfbathAvailability;
      filters.HalfbathAvailability = val ? "1" : "0";
    }

    // Legacy `{ half }` object encoding for half baths.
    {
      const halfBathNumHalf = asLegacyHalfBath(x?.half_bath_num)?.half;
      const halfBathAvailabilityHalf = asLegacyHalfBath(x?.HalfbathAvailability)?.half;
      if (halfBathNumHalf !== undefined || halfBathAvailabilityHalf !== undefined) {
        const halfValue =
          halfBathNumHalf !== undefined ? halfBathNumHalf : halfBathAvailabilityHalf;
        filters.HalfbathAvailability = halfValue ? "1" : "0";
      }
    }
  }

  if (x?.availability) {
    filters.Status =
      x.availability.length > 0 ? normalizeStatusValue(x.availability[0]) : "";
  } else if (x?.sold) {
    filters.Status = "closd";
  }
}


/**
 * Inverse of `SearchListingsOperationModel.fromFiltersToToolInput`: rebuild the
 * UI `IFilters` shape from the tool-call input. Mirrors `toUIFilters`, but reads
 * the tool-input field names (e.g. `mls_number`, `neighborhood_id`, the
 * `{ min, max }` ranges, and the individual amenity/quick-access booleans).
 */
export const fromToolInputToUIFilters = (x: ISearchListingsToolInput): IFilters => {
  const filters: IFilters = {};

  // --- Location ---
  if (x?.listing_agentid) {
    filters.listing_agentid = toLabeledField(x.listing_agentid);
  }

  const community = toCommunityField(x?.community);
  if (community) {
    filters.community = community;
  }

  if (x?.neighborhood_id != null) {
    filters.nid = toNeighborhoodField(x.neighborhood_id, String);
  }

  if (x?.zip_code) {
    filters.ZipCode = x.zip_code;
  }

  if (x?.subdivisions?.length) {
    filters.Subdivisions = x.subdivisions[0];
  }

  if (x?.streetaddress) {
    filters.Address = x.streetaddress;
  }

  if (x?.county_id != null) {
    // `county_id` is typed as a labeled field, but the server-authoritative
    // metadata sends it as a raw int (Python `Optional[int]`), and neither
    // shape carries a name. `toCountyField` resolves both and fills the label
    // from `countiesLookup` so the pill reads "Harris", not "48201".
    filters.County = toCountyField(x.county_id);
  }

  if (x?.city?.length) {
    filters.City = x.city;
  }

  if (x?.mls_number) {
    filters.MLSNumber = x.mls_number;
  }

  // --- Enums / scalars ---
  if (x?.finance) {
    // Canonicalize: the value may arrive percent-encoded or without the
    // `Seller to ` prefix, and both the pills and the `[name=Finance]` <select>
    // match `financeOptions` by strict equality.
    filters.Finance = normalizeFinanceValue(x.finance);
  }

  if (x?.property_type?.length) {
    filters.PropertyType = x.property_type;
  }

  // Quick-access category booleans -> `Category` (reverse of the `categoriesMap`
  // scan in `fromFiltersToToolInput`). The tool input names most of these after
  // the UI category, but not all — `Foreclosure` is `forcl` there — so resolve
  // the field name through the same mapping the write side uses.
  const categories: Set<QuickAccess> = new Set();
  for (const category of typedEntries(categoriesMap).map(([, value]) => value)) {
    if ((x as Record<string, unknown>)[toolInputKeyForQuickAccess(category)]) categories.add(category);
  }
  if (categories.size > 0) {
    filters.Category = Array.from(categories);
  }

  // --- Status / availability ---
  if (x?.availability) {
    filters.Status =
      x.availability.length > 0 ? normalizeStatusValue(x.availability[0]) : "";
  } else if (x?.sold) {
    filters.Status = "closd";
  }

  if (
    x?.for_rent !== undefined &&
    (!x?.sold ||
      (Array.isArray(x.availability) && x.availability.includes("closd")))
  ) {
    // `for_rent` inverts `for_sale`: 0 -> Sale, 1 -> Rent.
    filters.For = x.for_rent === 0 ? "Sale" : "Rent";
  }

  // --- Ranges: expand the `{ min, max }` objects back into flat fields ---
  if (x?.price?.min != null) filters.MinimumPrice = String(x.price.min);
  if (x?.price?.max != null) filters.MaximumPrice = String(x.price.max);

  if (x?.bedrooms_beds?.min != null) filters.BedroomMin = String(x.bedrooms_beds.min);
  if (x?.bedrooms_beds?.max != null) filters.BedroomMax = String(x.bedrooms_beds.max);

  if (x?.baths_bathrooms?.min != null) filters.FullbathMin = String(x.baths_bathrooms.min);
  if (x?.baths_bathrooms?.max != null) filters.FullbathMax = String(x.baths_bathrooms.max);

  if (x?.price_sqft?.min != null) filters.PricesqftMin = String(x.price_sqft.min);
  if (x?.price_sqft?.max != null) filters.PricesqftMax = String(x.price_sqft.max);

  // --- Numerics / scalars ---
  if (x?.half_bath_num != null) {
    filters.HalfbathAvailability = x.half_bath_num ? "1" : "0";
  }

  if (x?.hoa_fee_max != null) filters.HoafeeMax = String(x.hoa_fee_max);

  if (x?.style) filters.Style = x.style;
  if (x?.stories) filters.Stories = x.stories;
  if (x?.new_constr) filters.NewConstr = x.new_constr;
  if (x?.parking != null) filters.Parking = String(x.parking);
  if (x?.garage_num != null) filters.GarageNum = String(x.garage_num);
  if (x?.garage_desc) filters.GarageDesc = x.garage_desc;

  if (x?.year_built_min != null) filters.YearbuildsMin = String(x.year_built_min);
  if (x?.year_built_max != null) filters.YearbuildsMax = String(x.year_built_max);

  if (x?.square_feet_min != null) filters.buildingSQFTMin = String(x.square_feet_min);
  if (x?.square_feet_max != null) filters.buildingSQFTMax = String(x.square_feet_max);

  if (x?.lotsize_min != null) filters.LotsizeMin = String(x.lotsize_min);
  if (x?.lotsize_max != null) filters.LotsizeMax = String(x.lotsize_max);

  if (x?.acres_min != null) filters.LotAcresMin = String(x.acres_min);
  if (x?.acres_max != null) filters.LotAcresMax = String(x.acres_max);

  // --- Amenities: collapse the individual boolean flags into the array shape ---
  const amenities: FilterAmenities[] = [];
  for (const { value } of toolInputAmenitiesOptions) {
    if (value === "pool") continue; // handled via `pool`
    if (x[value]) amenities.push(value);
  }
  if (x?.pool === true) amenities.push("pool");
  else if (x?.pool === false) amenities.push("no_pool");
  if (amenities.length) filters.amenities = amenities;

  // --- Schools ---
  if (x?.elemantary_school_id) {
    filters.elemantary_school = toLabeledField(x.elemantary_school_id);
  }
  if (x?.middle_school_id) {
    filters.middle_school = toLabeledField(x.middle_school_id);
  }
  if (x?.high_school_id) {
    filters.high_school = toLabeledField(x.high_school_id);
  }
  if (x?.school_district) {
    filters.school_district = toLabeledField(x.school_district);
  }
  if (x?.school_id) {
    filters.school_id = toLabeledField(x.school_id);
  }

  // --- Geo (map-drawn bounds / polygon). `marketarea`/`latitude`/`longitude`/
  //     `radius` have no tool-input field of their own: they ride in `extra`
  //     (see `fromFiltersToToolInput`) and are restored with it below. ---
  if (x?.map_tools_nwlat != null) filters.map_tools_nwlat = x.map_tools_nwlat;
  if (x?.map_tools_nwlng != null) filters.map_tools_nwlng = x.map_tools_nwlng;
  if (x?.map_tools_selat != null) filters.map_tools_selat = x.map_tools_selat;
  if (x?.map_tools_selng != null) filters.map_tools_selng = x.map_tools_selng;
  if (x?.map_tools_polygon) filters.map_tools_polygon = x.map_tools_polygon;

  // --- Drive time. The tool input holds the travel time as a number; the modal's
  //     <select> wants the string. ---
  copyDriveTimeAsStrings(x, filters);

  filters.sort = x.sort;

  if(x?.extra) { filters.extra = { ...x.extra }; }
  promoteLegacyDriveTime(filters);

  fromToolInputToUIFiltersLegacy(filters, x as ISearchListingsToolInput.WithLegacy);
  return filters;
}

/**
 * Read the tool-input field names retired by CB-1756 out of chat history
 * persisted before the rename (see `ISearchListingsToolInput.Legacy`). Unlike
 * `toUIFiltersLegacy`, the current names win: these are the same values under
 * an older key, not a richer legacy shape, so they only fill a gap.
 */
export const fromToolInputToUIFiltersLegacy = (filters: IFilters, x: ISearchListingsToolInput.WithLegacy): void => {
  if (x?.address && !filters.Address) {
    filters.Address = x.address;
  }

  if (x?.Foreclosure) {
    const categories = new Set<QuickAccess>(filters.Category ?? []);
    categories.add("Foreclosure");
    filters.Category = Array.from(categories);
  }
}

// Reverse of `categoriesMap`: quick-access category value → the
// `ISearchListingsFilters` flag key that encodes it.
const reversedCategoriesMap = Object.fromEntries(
  typedEntries(categoriesMap).map(([key, value]) => [value, key]),
) as Record<QuickAccess, keyof typeof categoriesMap>;

/**
 * Inverse of `toUIFilters`: rebuild the `ISearchListingsFilters` (backend /
 * `restructure_filters`) shape from the UI `IFilters`. Legacy-only fields
 * (handled by `toUIFiltersLegacy`) are intentionally ignored.
 */
export function fromUIToFilters(input: IFilters): ISearchListingsFilters {
  const x: ISearchListingsFilters = {};

  if (input?.listing_agentid) {
    x.listing_agentid = toLabeledField(input.listing_agentid);
  }

  if (input?.community?.length) {
    x.community = input.community;
  }

  // `neighborhood` is the legacy UI key for the same id (the query-string
  // builder emits `nid` for either), and the tool payload only carries `nid` —
  // without the fallback a neighborhood picked from the AI summary was dropped
  // and the search re-ran unchanged. An explicit `nid` wins.
  const neighborhoodId = input?.nid ?? input?.neighborhood;
  if (neighborhoodId) {
    x.nid = toNeighborhoodField(neighborhoodId, Number);
  }

  if (input?.ZipCode) {
    x.zip_code = input.ZipCode.map(encodeURIComponent);
  }

  if (input?.Subdivisions) {
    x.subdivisions = encodeURIComponent(input.Subdivisions);
  }

  if (input?.Address) {
    x.streetaddress = encodeURIComponent(input.Address);
  }

  if (input?.County) {
    // Re-normalize rather than trusting the held value: `Number()` on a county
    // NAME yields NaN, which serializes to null and silently drops the filter.
    const county = toCountyField(input.County);
    if (county) { x.fips_code = toLabeledField(county.value, Number); }
  }

  if (input?.Finance) {
    x.finance = input.Finance;
  }

  if (input?.PropertyType) {
    x.property_class_id = input.PropertyType;
  }

  // Quick-access categories → individual flag keys (reverse of the
  // `categoriesMap` scan in `toUIFilters`).
  if (input?.Category?.length) {
    for (const category of input.Category) {
      const key = reversedCategoriesMap[category];
      if (!key) continue;
      if (key === "new_constr2") {
        x.new_constr2 = "Yes";
      } else {
        (x as Record<string, unknown>)[key] = 1;
      }
    }
  }

  if (input?.Status) {
    x.all_status = input.Status;
    if (input.Status === "closd") {
      x.sold = true;
    }
  }

  if (input?.For) {
    x.for_sale = input.For === "Sale" ? 1 : 0;
  }

  if (input?.City?.length) {
    x.city = input.City.map(encodeURIComponent).join(",");
  }

  if (input?.MLSNumber) {
    x.mlsnum = input.MLSNumber;
  }

  if (input?.MinimumPrice) {
    x.listing_price_min = Number(input.MinimumPrice);
  }
  if (input?.MaximumPrice) {
    x.listing_price_max = Number(input.MaximumPrice);
  }

  if (input?.BedroomMin) {
    x.bedroom_min = Number(input.BedroomMin);
  }
  if (input?.BedroomMax) {
    x.bedroom_max = Number(input.BedroomMax);
  }

  if (input?.FullbathMin) {
    x.full_bath_min = Number(input.FullbathMin);
  }
  if (input?.FullbathMax) {
    x.full_bath_max = Number(input.FullbathMax);
  }

  // Truthiness, not `!= null`: the dropdown's own values ("0"/"1") are both
  // truthy strings, while a cleared field ("") must be omitted rather than sent
  // as `half_bath_num: 0` — which reads as an explicit "no half bath" filter.
  if (input?.HalfbathAvailability) {
    x.half_bath_num = input.HalfbathAvailability === "1" ? 1 : 0;
  }

  if (input?.HoafeeMax) {
    x.hoa_fee_max = Number(input.HoafeeMax);
  }

  if (input?.Style) {
    x.style = input.Style;
  }

  if (input?.Stories) {
    // Same tolerance as `amenities`: normalize a comma-joined string to string[].
    x.stories = splitIntoArray(input.Stories);
  }

  if (input?.NewConstr) {
    x.new_constr = input.NewConstr;
  }

  if (input?.Parking) {
    x.parking = Number(input.Parking);
  }

  if (input?.GarageNum) {
    x.garage_num = Number(input.GarageNum);
  }

  if (input?.GarageDesc) {
    x.garage_desc = input.GarageDesc;
  }

  if (input?.YearbuildsMin) {
    x.year_built_min = Number(input.YearbuildsMin);
  }
  if (input?.YearbuildsMax) {
    x.year_built_max = Number(input.YearbuildsMax);
  }

  if (input?.buildingSQFTMin) {
    x.square_feet_min = Number(input.buildingSQFTMin);
  }
  if (input?.buildingSQFTMax) {
    x.square_feet_max = Number(input.buildingSQFTMax);
  }

  if (input?.PricesqftMin) {
    x.price_sqft_min = Number(input.PricesqftMin);
  }
  if (input?.PricesqftMax) {
    x.price_sqft_max = Number(input.PricesqftMax);
  }

  if (input?.LotsizeMin) {
    x.lotsize_min = Number(input.LotsizeMin);
  }
  if (input?.LotsizeMax) {
    x.lotsize_max = Number(input.LotsizeMax);
  }

  if (input?.amenities) {
    // Tolerate a comma-joined string: filters held in UI state (e.g. toggled in
    // the AI summary drawer) or restored from an older payload may arrive as
    // "pool,waterfront" rather than the declared FilterAmenities[].
    x.amenities = splitIntoArray(input.amenities).map((value): ToolFilterAmenities => {
      if(value === "pool") { return 'private_pool'; }
      else if(value === "no_pool") { return 'no_private_pool'; }
      return value;
    });
  }

  if (input?.LotAcresMin) x.acres_min = Number(input.LotAcresMin);
  if (input?.LotAcresMax) x.acres_max = Number(input.LotAcresMax);

  if (input?.elemantary_school) {
    x.schoolelementary = toLabeledField(input.elemantary_school);
  }
  if (input?.middle_school) {
    x.schoolmiddle = toLabeledField(input.middle_school);
  }
  if (input?.high_school) {
    x.schoolhigh = toLabeledField(input.high_school);
  }
  if (input?.school_district) {
    x.school_district = toLabeledField(input.school_district);
  }
  if (input?.school_id) {
    x.schoolid = toLabeledField(input.school_id, Number);
  }

  // --- Geo-scoping (market area / radius search / map bounds / drawn polygon) ---
  if (input?.marketarea) x.marketarea = input.marketarea;
  if (input?.latitude != null) x.latitude = input.latitude;
  if (input?.longitude != null) x.longitude = input.longitude;
  if (input?.radius != null) x.radius = input.radius;
  // The drawn shape and map bounds are copied as they are; the operation
  // model's fromFiltersToToolInput (the funnel for the modal and the URL seed
  // alike) drops them when a drive-time destination is set.
  if (input?.map_tools_nwlat != null) x.map_tools_nwlat = input.map_tools_nwlat;
  if (input?.map_tools_nwlng != null) x.map_tools_nwlng = input.map_tools_nwlng;
  if (input?.map_tools_selat != null) x.map_tools_selat = input.map_tools_selat;
  if (input?.map_tools_selng != null) x.map_tools_selng = input.map_tools_selng;
  if (input?.map_tools_polygon) x.map_tools_polygon = input.map_tools_polygon;

  // --- Drive time: copied as the modal holds it. Whether it is a drive-time
  //     search at all (a destination present, a whitespace-only address read as
  //     none, a usable travel time) is decided once, in the operation model's
  //     fromFiltersToToolInput, the funnel this output always goes through. ---
  copyDriveTimeAsStrings(input, x);

  x.sort = input.sort;

  if(input?.extra) { x.extra = { ...input.extra }; }

  return x;
}

/**
 * Similar to transformApiFilters, but including labels
 * @param input 
 */
export async function processUIFilters(input: ISearchListingsFilters.Raw): Promise<IFilters> {
  const filters = toUIFilters(input);

  // Collect relevant school IDs
  const schoolKeys = ['elemantary_school', 'middle_school', 'high_school', 'school_id'] as const satisfies ReadonlyArray<keyof IFilters>;
  const schoolIdsToFetch: { [K in typeof schoolKeys[number]]?: NonNullable<IFilters[K]>['value'] } = {};
  for (const key of schoolKeys) {
    const item = filters[key];
    if (item?.value) { schoolIdsToFetch[key] = item.value; }
  }

  // const schoolIdsToFetch: Pick<IFilters, typeof schoolKeys[number]> = {};
  // if (artifactFilters.school_district) schoolIdsToFetch.district = artifactFilters.school_district;
  // if (filters.elemantary_school) schoolIdsToFetch.elemantary_school = filters.elemantary_school;
  // if (filters.middle_school) schoolIdsToFetch.middle_school = filters.middle_school;
  // if (filters.high_school) schoolIdsToFetch.high_school = filters.high_school;
  // if (filters.school_id) schoolIdsToFetch.school_id = filters.school_id;

  // 🔁 Batch fetch once
  const allIds = Object.values(schoolIdsToFetch).map(String);
  const schoolsById = await getSchoolsDetailsByIds(allIds);
  filters.school_district = await resolveSchoolDistrictLabel(filters.school_district);

  // Apply results back into UI filters
  typedEntries(schoolIdsToFetch).forEach(([key, _id]) => {
    const id = _id!; // Forcing non-null since we filtered above
    const data = schoolsById[String(id)];
    const name = data?.name || id;

    // SchoolID - special case
    if(key === 'school_id')
    {
      // Use the new high, middle, elementary flags instead of grade array
      if (!filters.school_district && data?.grade?.includes("D")) {
        filters.school_district = { value: id, label: name };
      } else if (!filters.elemantary_school && data?.elementary === 1) {
        filters.elemantary_school = { value: id, label: name };
      } else if (!filters.middle_school && data?.middle === 1) {
        filters.middle_school = { value: id, label: name };
      } else if (!filters.high_school && data?.high === 1) {
        filters.high_school = { value: id, label: name };
      }

      return;
    }

    // Default Case
    filters[key] = { value: id, label: name };
  });

  
  const communityIds = filters.community;
  const communityId = communityIds?.[0]?.value;
  if (communityId) {
    const community = await getCommunityDetailsById(communityId);
    if (community) {
      const { name, id:_id } = community;
      const id = _id ? parseInt(_id) : undefined;
      filters.community = [{ value: id || communityId, label: name }];
    } else {
      filters.community = [{ value: communityId, label: "" }];
    }
  };
  const nId = filters?.nid?.value;
  if (nId) {
    const neighborhood = await getNeighborhoodDetailsById(nId, "nid");
    if (neighborhood) {
      const { name, nid, type } = neighborhood;
      if (type === "gm") {
        filters.marketarea = name;
        delete filters.nid;
      } else {
        filters.nid = { value: nid || nId, label: name };
      }
    } else {
      filters.nid = { value: nId, label: "" };
    }
  };

  return await processFiltersLegacy(filters);
}

async function processFiltersLegacy(filters: IFilters): Promise<IFilters> {
  const neighborhoodId = filters?.neighborhood?.value;
  if (neighborhoodId) {
    const neighborhood = await getNeighborhoodDetailsById(neighborhoodId, "ptId");
    if (neighborhood) {
      const { name, original_id } = neighborhood;
      filters.neighborhood = { value: original_id || neighborhoodId, label: name };
    } else {
      filters.neighborhood = { value: neighborhoodId, label: "" };
    }
  };

  return filters;
}

// tiny helper to guarantee nested objects exist
// function ensure<T extends object>(root: T, path: string): unknown {
//   const parts = path.split(".");
//   let node: any = root;
//   for (const p of parts) {
//     if (node[p] == null || typeof node[p] !== "object") node[p] = {};
//     node = node[p];
//   }
//   return node;
// }

// export async function transformQueryStringFiltersObjectToFilters(
//   listingFilters: IHARSearchListingsFilters,
// ): Promise<Partial<ISearchListingsFilters>> {
//   const transformedListingFilters: ISearchListingsFilters = {};

//   if (listingFilters?.ptids) {
//     const ptId = listingFilters.ptids;
//     const neighborhood = (await getNeighborhoodDetailsById(
//       ptId,
//       "ptId",
//     )) as INeighborhoodDetails;
//     if (neighborhood) {
//       const { name, original_id } = neighborhood;
//       transformedListingFilters.neighborhood = setValue(
//         name,
//         original_id || ptId,
//       );
//     } else {
//       transformedListingFilters.neighborhood = ptId;
//     }
//   }

//   if (listingFilters?.community) {
//     const communityId = parseInt(listingFilters.community);
//     const community = await getCommunityDetailsById(communityId);
//     if (community) {
//       const { name, id } = community;
//       transformedListingFilters.community = [new FilterPair(id || communityId, name)];
//     } else {
//       transformedListingFilters.community = [new FilterPair(communityId)];
//     }
//   }

//   if (listingFilters?.nid) {
//     const nId = listingFilters.nid;
//     const neighborhood = (await getNeighborhoodDetailsById(
//       nId,
//       "nid",
//     )) as INeighborhoodDetails;
//     if (neighborhood) {
//       const { name, nid } = neighborhood;
//       transformedListingFilters.nid = new FilterPair(nid || nId, name);
//     } else {
//       transformedListingFilters.nid = new FilterPair(nId);
//     }
//   }

//   if (listingFilters?.zip_code) {
//     transformedListingFilters.ZipCode =
//       listingFilters.zip_code
//         .split(",")
//         .map((z) => z.trim())
//         .filter(Boolean) || [];
//   }
//   if (listingFilters?.subdivisions) {
//     transformedListingFilters.Subdivisions = listingFilters.subdivisions;
//   }
//   if (listingFilters?.streetaddress) {
//     transformedListingFilters.StreetAddress = listingFilters.streetaddress;
//   }
//   if (listingFilters?.finance) {
//     transformedListingFilters.Finance = listingFilters.finance;
//   }
//   // property_class_id -> property_type[]
//   if (listingFilters?.property_class_id) {
//     // property_class_id=1,2,5,6,3,4
//     const propertyTypesArray =
//       listingFilters.property_class_id
//         .split(",")
//         .map((id) => reversedPropertyTypeObject[id])
//         .filter(Boolean) || [];
//     transformedListingFilters.property_type = propertyTypesArray;
//   }
//   // Category from quick access flags
//   const categoryKeys = typedKeys(categoriesMap);
//   const hasCategoryKeys = categoryKeys.some((key) => listingFilters?.[key]);
//   if (hasCategoryKeys) {
//     const categoryArray = categoryKeys
//       .filter((key) => listingFilters?.[key])
//       .map((key) => categoriesMap[key]);

//     transformedListingFilters.Category = categoryArray;
//   }
//   if (listingFilters?.all_status) {
//     // all_status=A,CS,OP,PS,P,closd,exp,term,WI
//     const statusArray =
//       listingFilters.all_status
//         .split(",")
//         .map((z) => normalizeStatusValue(z.trim()))
//         .filter(Boolean) || [];
//     transformedListingFilters.availability = statusArray[0];
//   }
//   if (listingFilters?.for_sale !== undefined) {
//     // transformedListingFilters.for_sale = listingFilters?.for_sale == '1' ? 1 : 0;
//     transformedListingFilters.for_sale = listingFilters.for_sale; // "1" | "0"
//   }
//   // city -> string[]
//   if (listingFilters?.city) {
//     transformedListingFilters.city = listingFilters.city
//       .split(",")
//       .map((c) => c.trim())
//       .filter(Boolean);
//   }
//   // county (from fips_code) as array (transformApiFilters supports county[])
//   if (listingFilters?.fips_code) {
//     transformedListingFilters.county = [listingFilters.fips_code];
//   }
//   // mls_number as array
//   if (listingFilters?.mlsnum) {
//     transformedListingFilters.mls_number = [listingFilters.mlsnum];
//   }
//   if (
//     listingFilters?.listing_price_min ||
//     listingFilters?.listing_price_max ||
//     listingFilters?.listing_price_equal
//   ) {
//     // ensure(transformedListingFilters, "price");
//     if (listingFilters?.listing_price_min) {
//       transformedListingFilters.price.min = listingFilters.listing_price_min;
//     }
//     if (listingFilters?.listing_price_max) {
//       transformedListingFilters.price.max = listingFilters.listing_price_max;
//     }
//     if (listingFilters?.listing_price_equal) {
//       transformedListingFilters.price.equal =
//         listingFilters.listing_price_equal;
//     }
//   }
//   if (
//     listingFilters?.bedroom_min ||
//     listingFilters?.bedroom_max ||
//     listingFilters?.bedroom_equal
//   ) {
//     // ensure(transformedListingFilters, "bedrooms_beds");
//     if (listingFilters?.bedroom_min) {
//       transformedListingFilters.bedrooms_beds.min = listingFilters.bedroom_min;
//     }
//     if (listingFilters?.bedroom_max) {
//       transformedListingFilters.bedrooms_beds.max = listingFilters.bedroom_max;
//     }
//     if (listingFilters?.bedroom_equal) {
//       transformedListingFilters.bedrooms_beds.equal =
//         listingFilters.bedroom_equal;
//     }
//   }
//   if (
//     listingFilters?.full_bath_min ||
//     listingFilters?.full_bath_max ||
//     listingFilters?.full_bath_equal ||
//     listingFilters?.half_bath_num != undefined ||
//     listingFilters?.HalfbathAvailability != undefined
//   ) {
//     // ensure(transformedListingFilters, "baths_bathrooms");
//     if (listingFilters?.full_bath_min) {
//       transformedListingFilters.baths_bathrooms.min =
//         listingFilters.full_bath_min;
//     }
//     if (listingFilters?.full_bath_max) {
//       transformedListingFilters.baths_bathrooms.max =
//         listingFilters.full_bath_max;
//     }
//     if (listingFilters?.full_bath_equal) {
//       transformedListingFilters.baths_bathrooms.equal =
//         listingFilters.full_bath_equal;
//     }
//     if (
//       listingFilters?.half_bath_num != null ||
//       listingFilters?.HalfbathAvailability != null
//     ) {
//       const halfBathValue =
//         listingFilters.half_bath_num ?? listingFilters.HalfbathAvailability;
//       const halfBathNumber = Number(halfBathValue);
//       transformedListingFilters.baths_bathrooms.half_bath_num = halfBathNumber;
//       transformedListingFilters.baths_bathrooms.half = halfBathNumber;
//       transformedListingFilters.HalfbathAvailability = halfBathNumber;
//     }
//   }
//   // simple numerics
//   if (listingFilters?.hoa_fee_max) {
//     transformedListingFilters.hoa_fee_max = listingFilters.hoa_fee_max;
//   }
//   if (listingFilters?.style) {
//     transformedListingFilters.style = listingFilters.style;
//   }
//   if (listingFilters?.stories) {
//     transformedListingFilters.stories = listingFilters.stories
//       .split(",")
//       .map((s) => s.trim())
//       .filter(Boolean);
//   }
//   if (listingFilters?.new_constr) {
//     transformedListingFilters.new_constr = listingFilters.new_constr;
//   }
//   if (listingFilters?.parking) {
//     transformedListingFilters.parking = listingFilters.parking;
//   }
//   if (listingFilters?.garage_num) {
//     transformedListingFilters.garage_num = listingFilters.garage_num;
//   }
//   if (listingFilters?.garage_desc) {
//     transformedListingFilters.garage_desc = listingFilters.garage_desc;
//   }
//   if (listingFilters?.year_built_min) {
//     transformedListingFilters.year_built_min = listingFilters.year_built_min;
//   }
//   if (listingFilters?.year_built_max) {
//     transformedListingFilters.year_built_max = listingFilters.year_built_max;
//   }
//   if (listingFilters?.square_feet_min) {
//     transformedListingFilters.square_feet_min = listingFilters.square_feet_min;
//   }
//   if (listingFilters?.square_feet_max) {
//     transformedListingFilters.square_feet_max = listingFilters.square_feet_max;
//   }

//   // ----- nested: price_sqft -----
//   if (
//     listingFilters?.price_sqft_min ||
//     listingFilters?.price_sqft_max
//   ) {
//     // ensure(transformedListingFilters, "price_sqft");
//     if (listingFilters?.price_sqft_min) {
//       transformedListingFilters.price_sqft.min = listingFilters.price_sqft_min;
//     }
//     if (listingFilters?.price_sqft_max) {
//       transformedListingFilters.price_sqft.max = listingFilters.price_sqft_max;
//     }
//     if (listingFilters?.price_sqft_equal) {
//       transformedListingFilters.price_sqft.equal =
//         listingFilters.price_sqft_equal;
//     }
//   }
//   if (listingFilters?.lotsize_min) {
//     transformedListingFilters.LotsizeMin = listingFilters?.lotsize_min;
//   }
//   if (listingFilters?.lotsize_max) {
//     transformedListingFilters.LotsizeMax = listingFilters?.lotsize_max;
//   }
//   if (listingFilters?.acres_min) {
//     transformedListingFilters.LotAcresMin = listingFilters.acres_min;
//   }
//   if (listingFilters?.acres_max) {
//     transformedListingFilters.LotAcresMax = listingFilters.acres_max;
//   }

//   // amenities from known keys (baseAmenitiesOptions.values)
//   let amenitiesArray: string[] = [];
//   const amenitiesKeys = baseAmenitiesOptions.map((item: any) => item.value);
//   amenitiesArray = amenitiesKeys.filter((key: any) =>
//     Object.prototype.hasOwnProperty.call(listingFilters, key),
//   );

//   // private_pool -> "pool" | "no_pool"
//   if (listingFilters?.private_pool != null) {
//     const privatePool =
//       listingFilters.private_pool === "1" ? "pool" : "no_pool";
//     amenitiesArray = [...amenitiesArray, privatePool];
//   }
//   if (amenitiesArray.length) {
//     transformedListingFilters.amenities = amenitiesArray;
//   }

//   // Handle school filters - fetch school names and use setValue
//   const schoolIdsToFetch: Record<string, string | number> = {};

//   // if (listingFilters?.school_district) {
//   //   schoolIdsToFetch.district = listingFilters.school_district;
//   // }
//   if (listingFilters?.schoolelementary) {
//     // NOTE: input key is "schoolelementary"
//     schoolIdsToFetch.elem = listingFilters.schoolelementary;
//   }
//   if (listingFilters?.schoolmiddle) {
//     schoolIdsToFetch.middle = listingFilters.schoolmiddle;
//   }
//   if (listingFilters?.schoolhigh) {
//     schoolIdsToFetch.high = listingFilters.schoolhigh;
//   }

//   if (listingFilters?.school_district) {
//     const schoolDistrictById = await getDistrictSchoolDetailsById(
//       listingFilters?.school_district!,
//     );
//     const { name = "", id = "" } = schoolDistrictById || {};
//     transformedListingFilters.school_district = setValue(name, id);
//   }

//   if (Object.keys(schoolIdsToFetch).length > 0) {
//     try {
//       const ids = Object.values(schoolIdsToFetch).map(String);
//       const schoolsById = await getSchoolsDetailsByIds(ids);

//       Object.entries(schoolIdsToFetch).forEach(([key, id]) => {
//         const data = schoolsById[String(id)];
//         const name = data?.name || id;

//         switch (key) {
//           // case "district":
//           //   transformedListingFilters.school_district = setValue(name, id);
//           //   break;
//           case "elem":
//             transformedListingFilters.elemantary_school = setValue(name, id);
//             break;
//           case "middle":
//             transformedListingFilters.middle_school = setValue(name, id);
//             break;
//           case "high":
//             transformedListingFilters.high_school = setValue(name, id);
//             break;
//         }
//       });
//     } catch (error) {
//       console.error("Error fetching school details (batch):", error);
//       // Fallback: set raw IDs directly (preserving existing keys)
//       if (listingFilters?.school_district) {
//         transformedListingFilters.school_district =
//           listingFilters.school_district;
//       }
//       if (listingFilters?.schoolelementary) {
//         transformedListingFilters.elemantary_school =
//           listingFilters.schoolelementary;
//       }
//       if (listingFilters?.schoolmiddle) {
//         transformedListingFilters.middle_school = listingFilters.schoolmiddle;
//       }
//       if (listingFilters?.schoolhigh) {
//         transformedListingFilters.high_school = listingFilters.schoolhigh;
//       }
//     }
//   }

//   return transformedListingFilters;
// };

export const extractFilterValuesAndAppendInQueryString = (
  q: URLSearchParams,
  f: IFilters,
) => {
  typedEntries(f).forEach(([key, value]) => {
    // console.log({ key, value });

    if (!value || SCHOOL_KEYS.includes(key as SchoolKey)) return;

    if (key === "neighborhood") q.append("nid", value.value);
    if (key === "nid") q.append("nid", value.value);
    if (key === "marketarea") q.append("marketarea", value);
    if (["forcl", "newconstr"].includes(key)) {
      q.append(key, "1");
    }

    if (key === "Stories") {
      // stories=1.5,2,3
      q.append("stories", splitIntoArray(value).join(","));
    }

    if (key === "City") {
      q.append(key.toLowerCase(), value.join(","));
    }

    if(key === 'Subdivisions' || key === 'Style' || key === 'Parking' || key === 'Finance') {
      q.append(key.toLowerCase(), value);
    }

    if (key === "listing_agentid") q.append("listing_agentid", value.value);
    if (key === "community") {
      // Removing the modal pill leaves `[]`, which is truthy (CB-1799).
      const community = Array.isArray(value) ? value[0] : value;
      if (community?.value != null) q.append("community", String(community.value));
    }
    if (key === "ZipCode") q.append("zip_code", value.join(","));
    if (key === "For") q.append("for_sale", value === "Sale" ? "1" : "0");
    if (key === "Address") q.append("streetaddress", value);
    if (key === "County") q.append("fips_code", String(value.value));
    if (key === "MLSNumber") q.append("mlsnum", value);
    if (key === "Status") {
      const statusValue = splitIntoArray(value)
        .map((s) => (s === "Active" ? "A" : s))
        .join(",");
      q.append("all_status", statusValue);
    }

    if (key === "PropertyType") {
      // HAR.com keys property type off the listing type: `property_class_id` (ids)
      // for sale, `prop_type` (rental codes) for rent. Read `f.For` rather than
      // relying on key order — `For` may be visited after `PropertyType`.
      if (f.For === "Rent") {
        // prop_type=SGL,THC
        q.append("prop_type", mapNamesToPropTypeCodes(value).join(","));
      } else {
        // property_class_id=1,2,5,6,3,4
        const propertyTypeIds = splitIntoArray(value)
          .map((v: string) => propertyTypeObject[v] || "")
          .join(",");
        q.append("property_class_id", propertyTypeIds);
      }
    }

    if (key === "Category") {
      // stype=1&pricereduced=1&forcl=1&new_constr2=Yes&open_houses=1&voh_only=1
      splitIntoArray(value).forEach((v: QuickAccess) => {
        // Temporarily exclude 'open_houses' from being appended here; it will be conditionally added below to prevent duplication.
        if (
          ["new_entry", "Foreclosure", "new_constr2", "open_houses"].includes(v)
        ) {
          if (v === "new_entry") q.append("stype", "1");
          if (v === "Foreclosure") q.append("forcl", "1");
          if (v === "new_constr2") q.append("new_constr2", "Yes");
        } else {
          q.append(v, "1");
        }
      });
    }

    if (key === "amenities") {
      // loft=1&patiodeck=1&mediarm=1&lake=1&wooded=1&greenbelt=1
      splitIntoArray(value).forEach((v: any) => {
        if (v === "pool") q.append("private_pool", "1");
        else if (v === "no_pool") q.append("private_pool", "0");
        else q.append(v, "1");
      });
    }

    if (key === "MinimumPrice") q.append("listing_price_min", value);
    if (key === "MaximumPrice") q.append("listing_price_max", value);

    if (key === "BedroomMin") q.append("bedroom_min", value);
    if (key === "BedroomMax") q.append("bedroom_max", value);

    if (key === "FullbathMin") q.append("full_bath_min", value);
    if (key === "FullbathMax") q.append("full_bath_max", value);

    if (key === "HalfbathAvailability") q.append("half_bath_num", value);

    if (key === "buildingSQFTMin") q.append("square_feet_min", value);
    if (key === "buildingSQFTMax") q.append("square_feet_max", value);

    if (key === "PricesqftMin") q.append("price_sqft_min", value);
    if (key === "PricesqftMax") q.append("price_sqft_max", value);

    if (key === "LotsizeMin") q.append("lotsize_min", value);
    if (key === "LotsizeMax") q.append("lotsize_max", value);

    if (key === "LotAcresMin") q.append("acres_min", value);
    if (key === "LotAcresMax") q.append("acres_max", value);

    if (key === "GarageNum") q.append("garage_num", value);
    if (key === "GarageDesc") q.append("garage_desc", value);

    if (key === "YearbuildsMin") q.append("year_built_min", value);
    if (key === "YearbuildsMax") q.append("year_built_max", value);

    if (key === "NewConstr") q.append("new_constr", value);

    if (key === "HoafeeMax") q.append("hoa_fee_max", value);

    // Geo scoping and drive time: same wire names on the UI and the query.
    if (GEO_FILTER_KEYS.includes(key as any) || DRIVETIME_FILTER_KEYS.includes(key as any)) {
      q.append(key, String(value));
    }

    if (
      //(key === "open_houses" && value) ||
      (key === "Category" &&
        Array.isArray(value) &&
        value.includes("open_houses"))
    ) {
      // Only append if not already added to prevent duplication
      if (!q.has("open_houses")) {
        q.append("open_houses", "1");
      }
    }
  });

  return q;
};